import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { authService } from '../services/authService';
import { getTokenPayload } from '../utils/auth';

export interface User {
  id: number;
  tenantId: number;
  warehouseId?: number | null;
  defaultWarehouseId?: number | null;
  activeWarehouseId?: number | null;
  canAccessAllWarehouses?: boolean;
  warehouse?: {
    id: number;
    name: string;
    status: string;
  } | null;
  actorTenantId?: number;
  effectiveTenantId?: number;
  isImpersonating?: boolean;
  tenant: {
    id: number;
    name: string;
    status: string;
  } | null;
  email: string;
  roleId: number;
  role: {
    id: number;
    name: string;
    description?: string | null;
    permissions: string[];
  } | null;
  permissions: string[];
  name: string | null;
  phone: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    tenantId?: number,
    warehouseId?: number,
  ) => Promise<{ requiresOtp: boolean; otpSessionId?: string; email?: string }>;
  verifyOtp: (otpSessionId: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  applySession: (session: {
    accessToken: string;
    refreshToken?: string;
    user: {
      id: number;
      tenantId: number;
      warehouseId?: number | null;
      defaultWarehouseId?: number | null;
      activeWarehouseId?: number | null;
      canAccessAllWarehouses?: boolean;
      warehouse?: User['warehouse'];
      actorTenantId?: number;
      effectiveTenantId?: number;
      isImpersonating?: boolean;
      tenant: User['tenant'];
      email: string;
      name: string | null;
      phone: string | null;
      status: string;
      createdAt: string;
      updatedAt: string;
      permissions: string[];
      role: NonNullable<User['role']>;
    };
  }) => void;
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  setActiveWarehouse: (warehouseId: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const normalizePermissions = (permissions: string[] | undefined | null) =>
    (permissions ?? []).map((permission) => permission.toLowerCase());

  const applySession = useCallback(
    (session: {
      accessToken: string;
      refreshToken?: string;
      user: {
        id: number;
        tenantId: number;
        warehouseId?: number | null;
        defaultWarehouseId?: number | null;
        activeWarehouseId?: number | null;
        canAccessAllWarehouses?: boolean;
        warehouse?: User['warehouse'];
        actorTenantId?: number;
        effectiveTenantId?: number;
        isImpersonating?: boolean;
        tenant: User['tenant'];
        email: string;
        name: string | null;
        phone: string | null;
        status: string;
        createdAt: string;
        updatedAt: string;
        permissions: string[];
        role: NonNullable<User['role']>;
      };
    }) => {
      localStorage.setItem('authToken', session.accessToken);
      if (session.refreshToken) {
        localStorage.setItem('refreshToken', session.refreshToken);
      }
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userEmail', session.user.email);
      if (session.user.activeWarehouseId) {
        localStorage.setItem(
          'activeWarehouseId',
          String(session.user.activeWarehouseId),
        );
      } else {
        localStorage.removeItem('activeWarehouseId');
      }

      const frontendUser: User = {
        id: session.user.id,
        tenantId: session.user.tenantId,
        warehouseId: session.user.warehouseId ?? null,
        defaultWarehouseId:
          session.user.defaultWarehouseId ?? session.user.warehouseId ?? null,
        activeWarehouseId:
          session.user.activeWarehouseId ??
          session.user.defaultWarehouseId ??
          session.user.warehouseId ??
          null,
        canAccessAllWarehouses: session.user.canAccessAllWarehouses ?? false,
        warehouse: session.user.warehouse ?? null,
        actorTenantId: session.user.actorTenantId,
        effectiveTenantId: session.user.effectiveTenantId ?? session.user.tenantId,
        isImpersonating: session.user.isImpersonating ?? false,
        tenant: session.user.tenant,
        email: session.user.email,
        roleId: session.user.role.id,
        role: {
          ...session.user.role,
          permissions: normalizePermissions(session.user.role.permissions),
        },
        name: session.user.name,
        permissions: normalizePermissions(session.user.permissions),
        phone: session.user.phone,
        status: session.user.status,
        createdAt: session.user.createdAt,
        updatedAt: session.user.updatedAt,
      };

      setUser(frontendUser);
      localStorage.setItem('userData', JSON.stringify(frontendUser));
    },
    [],
  );

  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      
      if (!token || !userData) {
        return false;
      }

      // Parse stored user data
      const parsedUser = JSON.parse(userData);
      
      // Validate user data structure
      if (!parsedUser || !parsedUser.id || !parsedUser.email) {
        throw new Error('Invalid user data structure');
      }

      const storedActiveWarehouseId = localStorage.getItem('activeWarehouseId');
      const payload = getTokenPayload(token);
      const restoredPermissions = normalizePermissions(
        parsedUser.permissions || parsedUser.role?.permissions || payload?.permissions,
      );

      setUser({
        ...parsedUser,
        activeWarehouseId: storedActiveWarehouseId
          ? Number(storedActiveWarehouseId)
          : parsedUser.activeWarehouseId ??
            parsedUser.defaultWarehouseId ??
            parsedUser.warehouseId ??
            null,
        permissions: restoredPermissions,
        role: parsedUser.role
          ? {
              ...parsedUser.role,
              permissions: restoredPermissions,
            }
          : null,
      });
      return true;
    } catch (error) {
      console.error('Auth check failed:', error);
      // Clear invalid data
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('activeWarehouseId');
      setUser(null);
      return false;
    }
  }, []);

  const login = useCallback(
    async (
      email: string,
      password: string,
      tenantId?: number,
      warehouseId?: number,
    ): Promise<{ requiresOtp: boolean; otpSessionId?: string; email?: string }> => {
      try {
        const response = await authService.login(email, password, tenantId, warehouseId) as any;
        if (response?.data?.requiresOtp) {
          return { requiresOtp: true, otpSessionId: response.data.otpSessionId, email: response.data.email };
        }
        applySession(response.data);
        return { requiresOtp: false };
      } catch (error) {
        throw error;
      }
    },
    [applySession],
  );

  const verifyOtp = useCallback(
    async (otpSessionId: string, code: string): Promise<void> => {
      const response = await authService.verifyOtp(otpSessionId, code);
      applySession((response as any).data);
    },
    [applySession],
  );

  const logout = async (): Promise<void> => {
    try {
      // Call backend logout endpoint
      await authService.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('activeWarehouseId');
      
      setUser(null);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      await checkAuth();
      setIsLoading(false);
    };

    initAuth();
  }, [checkAuth]);

  const setActiveWarehouse = useCallback((warehouseId: number) => {
    setUser((currentUser) => {
      if (!currentUser) {
        return currentUser;
      }

      const nextUser = {
        ...currentUser,
        activeWarehouseId: warehouseId,
      };

      localStorage.setItem('activeWarehouseId', String(warehouseId));
      localStorage.setItem('userData', JSON.stringify(nextUser));

      return nextUser;
    });
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    verifyOtp,
    logout,
    checkAuth,
    applySession,
    permissions: user?.permissions ?? [],
    hasPermission: (permission: string) =>
      (user?.permissions ?? []).includes(permission.toLowerCase()),
    hasAnyPermission: (permissions: string[]) =>
      permissions.some((permission) =>
        (user?.permissions ?? []).includes(permission.toLowerCase()),
      ),
    setActiveWarehouse,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
